import { useUsers } from "@/hooks/User";
import { useAuth } from "@/hooks/AuthContext";
import { useParams } from "react-router-dom";
import {
  Edit,
  X,
  DollarSign,
  Receipt,
  Download,
  AlertCircle,
  Clock,
  TrendingUp,
  Building,
  Calculator,
  Loader2,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import api from "@/api/axios";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { API, fmtDate, inr, ym, getStageColor } from "@/App";
import { KanbanBoard } from "@/components/ui/KanbanBoard";
import { MultiSelect } from "@/components/MultiSelectUsers";
import { StageInvoiceTable } from "@/components/StageInvoiceTable";
import Transmittals from "@/components/Transmittals";
import TaskManagerPage from "@/pages/TaskManager";
import { MonthPicker } from "@/components/month-picker";
import { ProjectTimelineView } from "@/components/ProjectTimelineView";

export function ProjectDetailsPage() {
  const users = useUsers();
  const { user } = useAuth();
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [team, setTeam] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [hours, setHours] = useState({ total_hours: 0, total_cost: 0 });
  const [receipts, setReceipts] = useState([]);
  const [transmittals, setTransmittals] = useState([]);
  const [activeTab, setActiveTab] = useState("team");
  const [editingTransmittal, setEditingTransmittal] = useState(null);
  const [mgmtMonth, setMgmtMonth] = useState(ym(new Date()));
  const [mgmt, setMgmt] = useState(null);
  const [timelineAllYears, setTimelineAllYears] = useState([]);
  const [timelineUsers, setTimelineUsers] = useState([]);
  const [timelineGrandTotals, setTimelineGrandTotals] = useState(null);
  const [timesheetMonth, setTimesheetMonth] = useState(ym(new Date()));
  const [timesheetScope, setTimesheetScope] = useState("month"); // "month" | "lifetime"
  const [timesheetBusy, setTimesheetBusy] = useState(false);

  const canManage = user?.role === "PM" || user?.role === "ADMIN";
  const showFinance = user?.role !== "TEAM";

  const load = async () => {
    const p = await api.get(`${API}/projects/${id}`).then((x) => x.data);
    setProject(p.project);
    console.log("project", p);
    setTeam(p.team);
    const [ts, hl, rc, tr] = await Promise.all([
      api
        .get(`${API}/tasks`, { params: { project_id: id } })
        .then((x) => x.data),
      api.get(`${API}/projects/${id}/hours-ledger`).then((x) => x.data),
      api
        .get(`${API}/receipts`, { params: { project_id: id } })
        .then((x) => x.data)
        .catch(() => []),
      api
        .get(`${API}/transmittals/${id}`)
        .then((x) => x.data)
        .catch(() => []),
    ]);
    setTasks(ts);
    setHours(hl);
    setReceipts(rc);
    setTransmittals(tr);
  };

  // const loadMgmt = async (m = mgmtMonth) => {
  //   const res = await api
  //     .get(`${API}/projects/${id}/mgmt-summary`, { params: { month: m } })
  //     .then((x) => x.data)
  //     .catch(() => null);
  //   setMgmt(res);
  // };

  const loadMgmt = async (m) => {
    const params = {};

    // only send month if a value is provided
    if (m) {
      params.month = m;
    }

    const res = await api
      .get(`${API}/projects/${id}/mgmt-summary`, { params })
      .then((x) => x.data)
      .catch(() => null);

    setMgmt(res);
    console.log(res);
  };

  useEffect(() => {
    if (id) {
      load();
      loadMgmt(mgmtMonth);
    }
  }, [id]);

  // useEffect(() => {
  //   if (id && activeTab === "mgmt") {
  //     loadMgmt(mgmtMonth);
  //   }
  // }, [mgmtMonth, activeTab, id]);

  useEffect(() => {
    if (id && activeTab === "mgmt") {
      loadMgmt();
    }
  }, [activeTab, id]);

  // load timeline data all years
  const loadTimeline = async () => {
    const res = await api
      .get(`${API}/projects/${id}/timeline-all-years`)
      .then((x) => x.data)
      .catch(() => null);

    if (res) {
      console.log("All timeline years data --->", res);
      setTimelineAllYears(res.data || []);
      setTimelineUsers(res.users || []);
      setTimelineGrandTotals(res.grandTotals || null);
    }
  };

  useEffect(() => {
    if (id && activeTab === "timeline") {
      loadTimeline();
    }
  }, [activeTab, id]);

  const teamUserIds = team.map((t) => t.user_id);
  const [assignForm, setAssignForm] = useState({
    user_id: "",
    role_on_project: "",
  });
  const addAssignment = async (e) => {
    e.preventDefault();
    if (!assignForm.user_id) return;
    await api.post(`${API}/projects/${id}/assignments`, assignForm);
    setAssignForm({ user_id: "", role_on_project: "" });
    await load();
  };
  const removeAssignment = async (aid) => {
    if (!canManage) return;
    await api.delete(`${API}/projects/${id}/assignments/${aid}`);
    await load();
  };

  const [taskForm, setTaskForm] = useState({
    title: "",
    priority: "Medium",
    estimate_hours: null,
    due_date: "",
    target: "PROJECT",
    assignee_id: "",
    team_id: "",
  });
  const createTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title) return;
    const payload = {
      project_id: id,
      title: taskForm.title,
      priority: taskForm.priority,
      estimate_hours: Number(taskForm.estimate_hours || 0),
      due_date: taskForm.due_date || null,
    };
    if (taskForm.target === "USER" && taskForm.assignee_id) {
      payload.assignee_id = taskForm.assignee_id;
      payload.scope = "USER";
    } else if (taskForm.target === "TEAM" && taskForm.team_id) {
      payload.team_id = taskForm.team_id;
      payload.scope = "TEAM";
    } else {
      payload.scope = "PROJECT";
    }
    await api.post(`${API}/tasks`, payload);
    setTaskForm({
      title: "",
      priority: "Medium",
      estimate_hours: 0,
      due_date: "",
      target: "PROJECT",
      assignee_id: "",
      team_id: "",
    });
    await load();
  };
  const updateTask = async (tid, patch) => {
    await api.post(`${API}/tasks/${tid}`, patch);
    await load();
  };

  const deleteTask = async (tid) => {
    if (window.confirm("Delete task?")) {
      await api.delete(`${API}/tasks/${tid}`);
      await load();
    }
  };

  const boardColumns = useMemo(
    () => [
      {
        key: "Backlog",
        title: "Backlog",
        items: tasks.filter((t) => t.status === "Backlog"),
      },
      {
        key: "In Progress",
        title: "In Progress",
        items: tasks.filter((t) => t.status === "In Progress"),
      },
      {
        key: "In Review",
        title: "In Review",
        items: tasks.filter((t) => t.status === "In Review"),
      },
      {
        key: "Done",
        title: "Done",
        items: tasks.filter((t) => t.status === "Done"),
      },
    ],
    [tasks],
  );
  const onDropTask = async (tid, newStatus) => {
    await updateTask(tid, { status: newStatus });
  };

  const handleCheckboxClick = () => {
    console.log("handle checked box clicked !");
  };

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1–12

  // The API returns `month` as a short name ("Jan".."Dec"), in this order.
  const TIMELINE_MONTH_NAMES = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  // Same filter the timeline table renders with, so the CSV matches the screen:
  // past years in full, the current year up to this month, nothing in the future.
  //
  // The month number is resolved by name lookup, NOT `new Date("Jan-01")` — that
  // string is not a format the spec requires engines to accept, and Firefox
  // returns Invalid Date for it, which made the comparison NaN <= month (always
  // false) and silently hid every month of the current year.
  const isVisibleTimelineMonth = (year, month) => {
    if (year < currentYear) return true;
    if (year > currentYear) return false;
    const monthNumber = TIMELINE_MONTH_NAMES.indexOf(month) + 1;
    if (monthNumber === 0) return true; // unrecognised label: show rather than hide
    return monthNumber <= currentMonth;
  };

  const csvCell = (value) => {
    if (value === null || value === undefined) return "";
    const safe = String(value).replace(/"/g, '""');
    return /[",\n]/.test(safe) ? `"${safe}"` : safe;
  };

  const saveCsv = (lines, filename) => {
    // \r\n and a UTF-8 BOM so Excel opens it cleanly and keeps the rupee sign.
    const blob = new Blob(["﻿" + lines.join("\r\n") + "\r\n"], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Per-day timesheet, laid out to match the sheet the client already works in:
  // one row per calendar day (weekends and idle days included), an hours + notes
  // column pair per person, then vouchers and a row total. Covers either a single
  // month or the project's whole life, depending on the selected scope.
  const downloadDailyTimesheetCsv = async () => {
    const lifetime = timesheetScope === "lifetime";
    setTimesheetBusy(true);
    try {
      const data = await api
        .get(`${API}/projects/${id}/daily-timesheet`, {
          params: lifetime ? { scope: "all" } : { month: timesheetMonth },
        })
        .then((r) => r.data);

      const people = data.users || [];
      const rows = data.entries || {};
      const code = data.project?.code ?? project?.code ?? "";

      if (!Object.keys(rows).length) {
        toast.error(
          lifetime
            ? "No time has been logged on this project yet."
            : `No time logged for ${timesheetMonth}.`
        );
        return;
      }

      const header = [
        "date",
        "day",
        ...people.flatMap((p) => [p, `${p} Notes`]),
        "Vouchers",
        "TOTAL",
      ];

      // Title cell above the header, padded so the row keeps the table's width.
      const period = lifetime ? `${data.from} to ${data.to}` : timesheetMonth;
      const title = [`${code} Timesheet_${period}`].concat(
        Array(header.length - 1).fill("")
      );
      const lines = [title.map(csvCell).join(","), header.map(csvCell).join(",")];

      const perPersonTotal = {};
      people.forEach((p) => (perPersonTotal[p] = 0));
      let voucherTotal = 0;
      let grandTotal = 0;

      Object.keys(rows)
        .sort()
        .forEach((date) => {
          const row = rows[date];
          people.forEach((p) => (perPersonTotal[p] += row.users?.[p] || 0));
          voucherTotal += row.vouchers || 0;
          grandTotal += row.total || 0;

          lines.push(
            [
              date,
              row.day,
              ...people.flatMap((p) => [
                row.users?.[p] || "",
                row.userNotes?.[p] || "",
              ]),
              row.vouchers || "",
              row.total || "",
            ]
              .map(csvCell)
              .join(",")
          );
        });

      lines.push(
        [
          "TOTAL",
          "-",
          ...people.flatMap((p) => [perPersonTotal[p] || "", ""]),
          voucherTotal || "",
          grandTotal || "",
        ]
          .map(csvCell)
          .join(",")
      );

      const projectName = data.project?.name || project?.name || "Project";
      const suffix = lifetime ? "Lifetime" : timesheetMonth.replace("-", "_");
      saveCsv(lines, `${projectName} Timesheet ${suffix}.csv`);
    } catch (err) {
      console.error("Failed to download daily timesheet:", err);
    } finally {
      setTimesheetBusy(false);
    }
  };

  const downloadTimelineCsv = () => {

    // Hours and cost get their own columns (the screen packs them into one cell)
    // so the numbers stay usable in Excel.
    const headers = [
      "Year",
      "Month",
      ...timelineUsers.flatMap((u) => [`${u} Hours`, `${u} Cost (INR)`]),
      "Total Hours",
      "Total Hour Cost (INR)",
      "Overhead (INR)",
    ];
    const lines = [headers.map(csvCell).join(",")];

    timelineAllYears.forEach((yearBlock) => {
      yearBlock.months
        .filter((row) => isVisibleTimelineMonth(yearBlock.year, row.month))
        .forEach((row) => {
          lines.push(
            [
              yearBlock.year,
              row.month,
              ...timelineUsers.flatMap((u) => [
                row.users?.[u] ?? 0,
                row.userCost?.[u] ?? 0,
              ]),
              row.totalHours ?? 0,
              row.totalCost ?? 0,
              row.overhead ?? 0,
            ]
              .map(csvCell)
              .join(","),
          );
        });

      lines.push(
        [
          yearBlock.year,
          `Total ${yearBlock.year}`,
          ...timelineUsers.flatMap((u) => [
            yearBlock.totals?.[u] ?? 0,
            yearBlock.totalsCost?.[u] ?? 0,
          ]),
          yearBlock.totals?.totalHours ?? 0,
          yearBlock.totals?.totalCost ?? 0,
          yearBlock.totals?.totalOverhead ?? 0,
        ]
          .map(csvCell)
          .join(","),
      );
    });

    if (timelineGrandTotals) {
      lines.push(
        [
          "",
          "Overall Total",
          ...timelineUsers.flatMap((u) => [
            timelineGrandTotals.users?.[u] ?? 0,
            timelineGrandTotals.userCost?.[u] ?? 0,
          ]),
          timelineGrandTotals.totalHours ?? 0,
          timelineGrandTotals.totalCost ?? 0,
          timelineGrandTotals.totalOverhead ?? 0,
        ]
          .map(csvCell)
          .join(","),
      );
    }

    saveCsv(lines, `Project_Timeline_${project?.code || id}.csv`);
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>
            Project:{" "}
            {project ? `${project.code} — ${project.name}` : "Loading..."}{" "}
            <Badge
              variant={"outline"}
              className={getStageColor(project?.current_stage_idx)}
            >
              {project?.current_stage_idx}
            </Badge>
          </h1>
          <p>Status: {project ? <Badge>{project.status}</Badge> : "—"}</p>
        </div>
      </div>

      <div className="tabs">
        {canManage && (
          <div className="tab-list mb-6">
            <button
              className={activeTab === "team" ? "tab active" : "tab"}
              onClick={() => setActiveTab("team")}
            >
              Dashboard
            </button>

            <button
              className={activeTab === "mgmt" ? "tab active" : "tab"}
              onClick={() => setActiveTab("mgmt")}
            >
              Management View
            </button>

            <button
              className={activeTab === "timeline" ? "tab active" : "tab"}
              onClick={() => setActiveTab("timeline")}
            >
              Timeline View
            </button>
          </div>
        )}

        {activeTab === "team" && (
          <div className="tab-panel">
            <Card>
              <CardHeader>
                <CardTitle>Team</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Member</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {team.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell>
                          {users.find((u) => u.id === t.user_id)?.name ||
                            t.user_id}
                        </TableCell>
                        <TableCell>{t.role_on_project || "—"}</TableCell>
                        <TableCell>
                          {canManage ? (
                            <Button
                              size="sm"
                              onClick={() => removeAssignment(t.id)}
                            >
                              Remove
                            </Button>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {team.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>No team assigned.</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {canManage && (
                  <form onSubmit={addAssignment} className="form-grid mt-4">
                    <div>
                      <Label>Add Member</Label>
                      <Select
                        value={assignForm.user_id}
                        onValueChange={(v) =>
                          setAssignForm({ ...assignForm, user_id: v })
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {users
                            .filter((u) => !teamUserIds.includes(u.id))
                            .map((u) => (
                              <SelectItem key={u.id} value={u.id}>
                                {u.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Role on Project</Label>
                      <Input
                        value={assignForm.role_on_project}
                        onChange={(e) =>
                          setAssignForm({
                            ...assignForm,
                            role_on_project: e.target.value,
                          })
                        }
                        placeholder="e.g., Architect"
                      />
                    </div>
                    <div className="col-span-2">
                      <Button type="submit" className="w-full">
                        Add to Team
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>

            <hr className="mt-5 mb-2" />
            <TaskManagerPage projectDetails={{ ...project, team }} />
            <Transmittals
              users={users}
              team={team}
              projectID={id}
              transmittals={transmittals}
              onDataChange={load}
            />
          </div>
        )}

        {activeTab === "mgmt" && (
          <div className="tab-panel">
            {/* <div className="row mb-3">
              <div className="controls">
                <Label>Month</Label>
                <MonthPicker value={mgmtMonth} onChange={setMgmtMonth} />
              </div>
            </div> */}

            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Financial Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                {mgmt ? (
                  <div className="grid grid-cols-3 gap-4">
                    {/* Fee Section */}
                    <div className="flex flex-col p-3 rounded-2xl border bg-muted/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Receipt className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Fee Total
                        </span>
                      </div>
                      <span className="text-lg font-semibold">
                        {/* {showFinance ? inr.format(mgmt.fee_total) : "—"} */}
                        {showFinance
                          ? inr.format(Math.round(mgmt.fee_total))
                          : "—"}
                      </span>
                    </div>

                    <div className="flex flex-col p-3 rounded-2xl border bg-muted/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Download className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Fees Received
                        </span>
                      </div>
                      <span className="text-lg font-semibold">
                        {/* {showFinance ? inr.format(mgmt.fees_received) : "—"} */}
                        {showFinance
                          ? inr.format(Math.round(mgmt.fees_received))
                          : "—"}
                      </span>
                    </div>

                    <div className="flex flex-col p-3 rounded-2xl border bg-muted/20">
                      <div className="flex items-center gap-2 mb-1">
                        <AlertCircle className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Outstanding
                        </span>
                      </div>
                      <span className="text-lg font-semibold">
                        {/* {showFinance ? inr.format(mgmt.outstanding) : "—"} */}
                        {showFinance
                          ? inr.format(Math.round(mgmt.outstanding))
                          : "—"}
                      </span>
                    </div>

                    {/* Hours & Productivity */}
                    <div className="flex flex-col p-3 rounded-2xl border bg-muted/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Total Hours
                        </span>
                      </div>
                      <span className="text-lg font-semibold">
                        {/* {mgmt.totals.hours} */}
                        {Math.round(mgmt.totals.hours)}
                      </span>
                    </div>

                    {/* Cost Breakdown */}
                    <div className="flex flex-col p-3 rounded-2xl border bg-muted/20">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          Direct Cost
                        </span>
                      </div>
                      <span className="text-lg font-semibold">
                        {/* {showFinance
                          ? inr.format(mgmt.totals.direct_cost)
                          : "—"} */}
                        {showFinance
                          ? inr.format(Math.round(mgmt.totals.direct_cost))
                          : "—"}
                      </span>
                    </div>

                    <div className="flex flex-col p-3 rounded-2xl border bg-muted/20">
                      <div className="flex items-center gap-2 mb-1">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        {/* <span className="text-sm text-muted-foreground">Overhead (Month)</span> */}
                        <span className="text-sm text-muted-foreground">
                          Overhead
                        </span>
                      </div>
                      <span className="text-lg font-semibold">
                        {/* {showFinance
                          ? inr.format(mgmt.totals?.total_overhead_all_months)
                          : "—"} */}
                        {showFinance
                          ? inr.format(
                              Math.round(
                                mgmt.totals?.total_overhead_all_months,
                              ),
                            )
                          : "—"}
                      </span>
                    </div>

                    {/* Total Summary */}
                    <div className="flex flex-col p-3 rounded-2xl border bg-primary/5 border-primary/20 col-span-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Calculator className="h-4 w-4 text-primary" />
                        <span className="text-sm text-muted-foreground">
                          Total Cost
                        </span>
                        {/* <span className="text-sm text-muted-foreground">Total Cost (Month)</span > */}
                      </div>
                      <span className="text-xl font-bold text-primary">
                        {/* {showFinance
                          ? inr.format(mgmt.totals?.total_cost_of_project)
                          : "—"} */}
                        {showFinance
                          ? inr.format(
                              Math.round(mgmt.totals?.total_cost_of_project),
                            )
                          : "—"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted-foreground py-6 flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                )}
              </CardContent>
            </Card>

            {/* //Show monthly breakdown  */}
            {/* <Card className="mt-4">
              <CardHeader>
                <CardTitle>
                  {" "}
                  Monthly OPEX Breakdown ({new Date().getFullYear()})
                </CardTitle>
              </CardHeader>

              <CardContent>
                {mgmt?.totals?.overhead_breakdown_per_month ? (
                  (() => {
                    const data = mgmt.totals.overhead_breakdown_per_month;

                    const months = [
                      { key: "01", name: "Jan" },
                      { key: "02", name: "Feb" },
                      { key: "03", name: "Mar" },
                      { key: "04", name: "Apr" },
                      { key: "05", name: "May" },
                      { key: "06", name: "Jun" },
                      { key: "07", name: "Jul" },
                      { key: "08", name: "Aug" },
                      { key: "09", name: "Sep" },
                      { key: "10", name: "Oct" },
                      { key: "11", name: "Nov" },
                      { key: "12", name: "Dec" },
                    ];

                    return (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Month</TableHead>
                            <TableHead className="text-right">
                              OPEX Total
                            </TableHead>
                          </TableRow>
                        </TableHeader>

                        <TableBody>
                          {months.map((m) => {
                            const rawEntries = Object.entries(data)
                              .filter(([monthKey]) =>
                                monthKey.endsWith(`-${m.key}`),
                              )
                              .flatMap(([_, arr]) => arr);

                            const monthTotal = rawEntries.reduce(
                              (sum, item) =>
                                sum + (parseFloat(item.total) || 0),
                              0,
                            );

                            return (
                              <TableRow key={m.key}>
                                <TableCell>{m.name}</TableCell>
                                <TableCell className="text-right font-semibold">
                                  {showFinance ? inr.format(monthTotal) : "—"}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    );
                  })()
                ) : (
                  <div className="text-sm text-muted-foreground">
                    No OPEX data found.
                  </div>
                )}
              </CardContent>
            </Card> */}

            <Card className="mt-4">
              <CardHeader>
                <CardTitle>Per-Stage Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {
                  <StageInvoiceTable
                    mgmt={mgmt}
                    project={project}
                    showFinance={showFinance}
                    refresh={() => {
                      loadMgmt();
                      load();
                    }}
                  />
                }
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "timeline" && (
          <div className="tab-panel">
            <Card className="mb-4">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Project Timeline</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={timesheetScope} onValueChange={setTimesheetScope}>
                    <SelectTrigger className="w-[130px] h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="month">Monthly</SelectItem>
                      <SelectItem value="lifetime">Lifetime</SelectItem>
                    </SelectContent>
                  </Select>

                  {timesheetScope === "month" && (
                    <div className="w-[150px]">
                      <MonthPicker
                        value={timesheetMonth}
                        onChange={setTimesheetMonth}
                        hideIcon
                      />
                    </div>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={downloadDailyTimesheetCsv}
                    disabled={
                      timesheetBusy ||
                      (timesheetScope === "month" && !timesheetMonth)
                    }
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {timesheetBusy ? "Preparing..." : "Timesheet CSV"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={downloadTimelineCsv}
                    disabled={timelineAllYears.length === 0}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Timeline CSV
                  </Button>
                </div>
              </CardHeader>

              <CardContent>
                {timelineAllYears.length === 0 && (
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell className="text-center text-muted-foreground">
                          No timeline data available
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                )}

                {timelineAllYears.map((yearBlock, index) => (
                  <div key={yearBlock.year} className="mb-8">
                    {/* YEAR TITLE */}
                    <div className="font-bold text-lg mb-2">
                      {yearBlock.year}
                    </div>

                    <Table className="w-full table-fixed">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>

                          {timelineUsers.map((u) => (
                            <TableHead key={u}>{u}</TableHead>
                          ))}

                          <TableHead className="text-right">
                            Total Hours
                          </TableHead>

                          <TableHead className="text-right">
                            {/* Total Cost */}
                            Total Hour Cost
                          </TableHead>

                          <TableHead className="text-right">Overhead</TableHead>
                        </TableRow>
                      </TableHeader>

                      <TableBody>
                        {/* MONTH ROWS */}
                        {yearBlock.months
                          .filter((row) =>
                            isVisibleTimelineMonth(yearBlock.year, row.month),
                          )
                          .map((row) => (
                            <TableRow key={`${yearBlock.year}-${row.month}`}>
                              <TableCell>{row.month}</TableCell>

                              {timelineUsers.map((u) => {
                                const hrs = row.users?.[u] ?? 0;
                                const cost = row.userCost?.[u] ?? 0;

                                return (
                                  <TableCell key={u}>
                                    {hrs}{" "}
                                    <span className="text-xs text-muted-foreground">
                                      ({inr.format(cost)})
                                    </span>
                                  </TableCell>
                                );
                              })}

                              <TableCell className="text-right font-medium">
                                {row.totalHours ?? 0}
                              </TableCell>

                              <TableCell className="text-right font-semibold">
                                {inr.format(row.totalCost ?? 0)}
                              </TableCell>

                              <TableCell className="text-right">
                                {inr.format(row.overhead ?? 0)}
                              </TableCell>
                            </TableRow>
                          ))}

                        {/* YEAR TOTAL */}
                        <TableRow className="bg-muted/50 font-semibold">
                          <TableCell>Total {yearBlock.year}</TableCell>

                          {timelineUsers.map((u) => {
                            const hrs = yearBlock.totals?.[u] ?? 0;
                            const cost = yearBlock.totalsCost?.[u] ?? 0;

                            return (
                              <TableCell key={u}>
                                {hrs}{" "}
                                <span className="text-xs text-muted-foreground">
                                  ({inr.format(cost)})
                                </span>
                              </TableCell>
                            );
                          })}

                          <TableCell className="text-right">
                            {yearBlock.totals?.totalHours ?? 0}
                          </TableCell>

                          <TableCell className="text-right">
                            {inr.format(yearBlock.totals?.totalCost ?? 0)}
                          </TableCell>

                          <TableCell className="text-right">
                            {inr.format(yearBlock.totals?.totalOverhead ?? 0)}
                          </TableCell>
                        </TableRow>

                        {/* GRAND TOTAL */}
                        {index === timelineAllYears.length - 1 &&
                          timelineGrandTotals && (
                            <TableRow className="bg-green-100 text-green-800 font-bold">
                              <TableCell>Overall Total</TableCell>

                              {timelineUsers.map((u) => {
                                const hrs = timelineGrandTotals.users?.[u] ?? 0;
                                const cost =
                                  timelineGrandTotals.userCost?.[u] ?? 0;

                                return (
                                  <TableCell key={u}>
                                    {hrs}{" "}
                                    <span className="text-xs text-muted-foreground">
                                      ({inr.format(cost)})
                                    </span>
                                  </TableCell>
                                );
                              })}

                              <TableCell className="text-right">
                                {timelineGrandTotals.totalHours ?? 0}
                              </TableCell>

                              <TableCell className="text-right">
                                {inr.format(timelineGrandTotals.totalCost ?? 0)}
                              </TableCell>

                              <TableCell className="text-right">
                                {inr.format(
                                  timelineGrandTotals.totalOverhead ?? 0,
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                      </TableBody>
                    </Table>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
